import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Navigation, Globe, Compass, MapPin, Building2, ChevronRight, Loader2, ExternalLink, Utensils, Wine, BedDouble, Lightbulb, ScanLine } from "lucide-react";
import { api } from "../api";
import { resolveImageUrl } from "../lib/imageUrl";
import { BrandMark } from "../components/Brand";

const NEARBY_RADIUS_KM = 200;

function haversineKm(a, b) {
  const R = 6371;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
}

function openMapsSearch(query, near) {
  const q = encodeURIComponent(query);
  const url = near
    ? `https://www.google.com/maps/search/?api=1&query=${q}&center=${near.lat},${near.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${q}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function requestGeolocation() {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("Geolocalizzazione non disponibile su questo dispositivo."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => {
        const msgs = {
          1: "Permesso negato — abilita la posizione nelle impostazioni del browser.",
          2: "Posizione non disponibile.",
          3: "Richiesta scaduta. Riprova.",
        };
        reject(new Error(msgs[err.code] || err.message || "Errore geolocalizzazione"));
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  });
}

export default function Home() {
  const navigate = useNavigate();
  const [museums, setMuseums] = useState([]);
  const [loadingMuseums, setLoadingMuseums] = useState(true);
  const [geo, setGeo] = useState({ kind: "idle" });
  const [manualCode, setManualCode] = useState("");

  useEffect(() => {
    api.listMuseums()
      .then((m) => { setMuseums(m); setLoadingMuseums(false); })
      .catch(() => setLoadingMuseums(false));
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      setGeo({ kind: "loading" });
      requestGeolocation()
        .then((c) => setGeo({ kind: "ok", coords: c }))
        .catch((e) => setGeo({ kind: "denied", message: e.message }));
    }
  }, []);

  const requestGeo = () => {
    setGeo({ kind: "loading" });
    requestGeolocation()
      .then((c) => setGeo({ kind: "ok", coords: c }))
      .catch((e) => setGeo({ kind: "denied", message: e.message }));
  };

  const userCoord = geo.kind === "ok" ? geo.coords : null;

  const decorated = useMemo(() => {
    if (!userCoord) return museums.map((m) => ({ m, distKm: null }));
    return museums
      .map((m) => {
        if (typeof m.lat !== "number" || typeof m.lng !== "number") return { m, distKm: null };
        return { m, distKm: haversineKm(userCoord, { lat: m.lat, lng: m.lng }) };
      })
      .sort((a, b) => {
        if (a.distKm === null && b.distKm === null) return 0;
        if (a.distKm === null) return 1;
        if (b.distKm === null) return -1;
        return a.distKm - b.distKm;
      });
  }, [museums, userCoord]);

  const nearby = userCoord
    ? decorated.filter((x) => x.distKm !== null && x.distKm <= NEARBY_RADIUS_KM)
    : [];

  function submitManualCode(e) {
    e.preventDefault();
    const c = manualCode.trim().toUpperCase();
    if (c) navigate(`/q/${encodeURIComponent(c)}`);
  }

  return (
    <div className="min-h-screen bg-bg page-enter">
      <div className="max-w-2xl mx-auto px-5 py-8">
        {/* Brand row */}
        <div className="flex items-center gap-3 mb-10">
          <BrandMark />
          <span className="font-heading font-bold text-2xl tracking-tight text-ink flex-1">QrTourist</span>
          <Link
            to="/admin/login"
            data-testid="landing-admin-link"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-border text-ink-soft text-[11px] font-medium tracking-widest hover:border-brand/40 transition"
          >
            <Lock size={11} strokeWidth={2.5} />
            ADMIN
          </Link>
        </div>

        {/* Hero */}
        <div className="mb-10">
          <div className="text-[11px] tracking-[0.3em] font-semibold text-brand mb-3">L'ARTE RACCONTATA</div>
          <h1 className="font-heading font-bold text-5xl sm:text-6xl leading-[0.95] tracking-tight text-ink">
            Scansiona.<br />Ascolta. Scopri.
          </h1>
          <p className="text-ink-soft mt-5 leading-relaxed text-[15px]">
            Nessuna app da scaricare. Inquadra il QR code accanto ad un'opera o in un borgo d'arte per iniziare.
          </p>
        </div>

        {/* Manual QR entry */}
        <form onSubmit={submitManualCode} className="mb-8 flex gap-2 bg-white border border-border rounded-2xl p-2 shadow-sm">
          <div className="flex items-center px-3 text-ink-soft">
            <ScanLine size={18} />
          </div>
          <input
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="Hai un codice QR? es. QRT-XXXX"
            className="flex-1 bg-transparent outline-none text-ink font-medium placeholder:text-ink-soft/60"
            data-testid="manual-qr-input"
          />
          <button
            type="submit"
            data-testid="manual-qr-submit"
            className="px-4 py-2.5 rounded-xl bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition"
          >
            Apri
          </button>
        </form>

        {/* Nearby */}
        <div className="flex items-center gap-2 mt-2 mb-1">
          <MapPin className="text-brand" size={18} />
          <h2 className="font-semibold text-lg text-ink flex-1">Vicino a te</h2>
          {geo.kind === "loading" && <Loader2 size={14} className="animate-spin text-brand" />}
        </div>

        {geo.kind === "loading" && (
          <div className="flex items-center gap-3 bg-white border border-border rounded-2xl p-4 mb-3">
            <Loader2 className="animate-spin text-brand" size={18} />
            <span className="text-sm text-ink-soft">Stiamo individuando la tua posizione…</span>
          </div>
        )}
        {(geo.kind === "denied" || geo.kind === "idle") && (
          <button
            onClick={requestGeo}
            data-testid="enable-geo-btn"
            className="w-full flex items-center gap-3 rounded-2xl p-4 mb-3 bg-gold/5 border border-gold/30 hover:bg-gold/10 transition text-left"
          >
            <Navigation className="text-gold shrink-0" size={20} />
            <div className="flex-1">
              <div className="font-semibold text-sm text-ink">
                {geo.kind === "denied" ? "Attiva la posizione" : "Mostra solo destinazioni vicine"}
              </div>
              <div className="text-xs text-ink-soft mt-0.5">
                {geo.kind === "denied" ? geo.message : `Solo destinazioni raggiungibili in giornata (entro ${NEARBY_RADIUS_KM} km).`}
              </div>
            </div>
            <ChevronRight className="text-ink-soft" size={16} />
          </button>
        )}

        {loadingMuseums ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
        ) : userCoord && nearby.length > 0 ? (
          <>
            <div className="text-xs text-ink-soft mb-2">
              {nearby.length} {nearby.length === 1 ? "destinazione" : "destinazioni"} entro {NEARBY_RADIUS_KM} km
            </div>
            {nearby.map(({ m, distKm }) => <DestinationRow key={m.id} m={m} distKm={distKm} />)}
          </>
        ) : userCoord && nearby.length === 0 ? (
          <div className="bg-white border border-border rounded-2xl p-6 text-center mb-3">
            <Globe className="mx-auto text-ink-soft mb-2" size={28} />
            <div className="font-semibold text-sm text-ink">Nessuna destinazione entro {NEARBY_RADIUS_KM} km</div>
            <div className="text-xs text-ink-soft mt-1.5 leading-relaxed">
              Stiamo aggiungendo nuovi musei e borghi ogni settimana. Esplora tutte le destinazioni qui sotto.
            </div>
          </div>
        ) : null}

        {/* All destinations (if denied or no nearby) */}
        {!loadingMuseums && (geo.kind === "denied" || (userCoord && nearby.length === 0)) && (
          <>
            <div className="flex items-center gap-2 mt-6 mb-2">
              <Globe className="text-ink-soft" size={16} />
              <h3 className="font-semibold text-base text-ink">Tutte le destinazioni</h3>
            </div>
            {decorated.map(({ m, distKm }) => <DestinationRow key={m.id} m={m} distKm={distKm} />)}
          </>
        )}

        {!loadingMuseums && geo.kind !== "denied" && !userCoord && (
          <>
            <div className="flex items-center gap-2 mt-6 mb-2">
              <Globe className="text-ink-soft" size={16} />
              <h3 className="font-semibold text-base text-ink">Destinazioni</h3>
            </div>
            {decorated.map(({ m, distKm }) => <DestinationRow key={m.id} m={m} distKm={distKm} />)}
          </>
        )}

        {/* POI buttons */}
        <div className="flex items-center gap-2 mt-8 mb-1">
          <Compass className="text-brand" size={18} />
          <h2 className="font-semibold text-lg text-ink flex-1">Cosa c'è in zona</h2>
        </div>
        <p className="text-xs text-ink-soft mb-3">
          {userCoord ? "Apri Google Maps con la tua posizione" : "Apri Google Maps · attiva la posizione per risultati locali"}
        </p>
        <div className="grid grid-cols-3 gap-2.5 mb-8">
          <PoiBtn icon={<Utensils size={22} />} label="Ristoranti" color="#E11D48" onClick={() => openMapsSearch("ristoranti", userCoord)} testId="poi-ristoranti" />
          <PoiBtn icon={<Wine size={22} />} label="Bar" color="#7C3AED" onClick={() => openMapsSearch("bar caffè", userCoord)} testId="poi-bar" />
          <PoiBtn icon={<BedDouble size={22} />} label="Hotel" color="#0891B2" onClick={() => openMapsSearch("hotel B&B", userCoord)} testId="poi-hotel" />
        </div>

        {/* How it works */}
        <div className="bg-white rounded-2xl border border-border p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="text-gold" size={16} />
            <span className="text-[11px] tracking-[0.2em] font-semibold text-ink">COME FUNZIONA</span>
          </div>
          <Step n="1" title="Inquadra" desc="Apri la fotocamera sul QR accanto all'opera." />
          <Step n="2" title="Ascolta l'anteprima" desc="Preview gratuita in 5 lingue." />
          <Step n="3" title="Sblocca la storia" desc="Pass singolo o Pass Travel di tutta la città." />
        </div>

        <div className="text-center pb-8">
          <a href="mailto:info@qrtourist.it" className="text-sm text-ink-soft underline hover:text-brand">
            Hai bisogno di aiuto? Scrivici
          </a>
        </div>
      </div>
    </div>
  );
}

function DestinationRow({ m, distKm }) {
  const src = resolveImageUrl(m.image_url);
  return (
    <Link
      to={`/museo/${m.id}`}
      data-testid={`landing-museum-${m.id}`}
      className="flex items-center gap-4 bg-white border border-border rounded-2xl p-3 mb-2.5 hover:border-brand/30 hover:shadow-sm transition group"
    >
      {src ? (
        <img
          src={src}
          alt={m.name}
          onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          className="w-14 h-14 rounded-xl object-cover"
        />
      ) : null}
      <div className={`w-14 h-14 rounded-xl bg-muted items-center justify-center ${src ? "hidden" : "flex"}`}>
        <Building2 className="text-ink-soft" size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <div className="font-semibold text-[15px] text-ink truncate">{m.name}</div>
          {m.kind === "city" && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gold/15 text-gold rounded-full text-[10px] font-semibold tracking-wider">
              <MapPin size={9} />BORGO
            </span>
          )}
        </div>
        <div className="text-xs text-ink-soft mt-0.5 truncate">
          {[m.city, m.region].filter(Boolean).join(" · ")}
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-[11px] font-medium text-brand">
            {m.artwork_count} {m.artwork_count === 1 ? "opera" : "opere"}
          </span>
          {distKm !== null && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand/10 text-brand rounded-full text-[10px] font-semibold">
              <Navigation size={9} />{distKm < 10 ? distKm.toFixed(1) : Math.round(distKm)} km
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="text-ink-soft group-hover:text-brand transition" size={18} />
    </Link>
  );
}

function PoiBtn({ icon, label, color, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="flex flex-col items-center justify-center gap-2 bg-white border border-border rounded-2xl py-4 px-2 hover:shadow-md transition"
    >
      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: color }}>
        {icon}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[13px] font-semibold text-ink">{label}</span>
        <ExternalLink size={11} className="text-ink-soft" />
      </div>
    </button>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center font-bold text-sm shrink-0">{n}</div>
      <div>
        <div className="font-semibold text-sm text-ink">{title}</div>
        <div className="text-xs text-ink-soft">{desc}</div>
      </div>
    </div>
  );
}
